import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Clock, FileText } from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator
} from '../ui/command';
import { Badge } from '../ui/badge';
import useSearchStore from '../../store/useSearchStore';
import { useDebounce } from '../../hooks/useDebounce';

export function SearchDialog() {
  const navigate = useNavigate();
  const {
    isOpen,
    setOpen,
    query,
    setQuery,
    results,
    isLoading,
    totalCount,
    recentSearches,
    search,
    addRecentSearch,
    close
  } = useSearchStore();

  const debouncedQuery = useDebounce(query, 300);

  // Register global keyboard shortcut
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!isOpen);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [isOpen, setOpen]);

  // Trigger search when debounced query changes
  useEffect(() => {
    if (debouncedQuery && debouncedQuery.length >= 2) {
      search(debouncedQuery);
    } else if (!debouncedQuery) {
      // Clear results when query is empty
      useSearchStore.setState({ results: [], totalCount: 0 });
    }
  }, [debouncedQuery, search]);

  const handleSelect = useCallback((result) => {
    // Add to recent searches
    addRecentSearch(query);
    // Navigate to task
    navigate(`/projects/${result.projectId}/tasks?taskId=${result.id}`);
    // Close dialog
    close();
  }, [navigate, close, addRecentSearch, query]);

  const handleRecentSearch = useCallback((recentQuery) => {
    setQuery(recentQuery);
  }, [setQuery]);

  const handleOpenChange = useCallback((open) => {
    setOpen(open);
  }, [setOpen]);

  // Get status color based on status name
  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    const name = status.toLowerCase();
    if (name.includes('done') || name.includes('complete')) {
      return 'bg-green-100 text-green-800';
    }
    if (name.includes('progress') || name.includes('working')) {
      return 'bg-blue-100 text-blue-800';
    }
    if (name.includes('review') || name.includes('testing')) {
      return 'bg-yellow-100 text-yellow-800';
    }
    if (name.includes('blocked') || name.includes('hold')) {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  // Truncate text helper
  const truncate = (text, maxLength = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <CommandDialog open={isOpen} onOpenChange={handleOpenChange}>
      <CommandInput
        placeholder="Search tasks..."
        value={query}
        onValueChange={setQuery}
        data-testid="search-input"
      />
      <CommandList>
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && query.length >= 2 && results.length === 0 && (
          <CommandEmpty>No results found for "{query}"</CommandEmpty>
        )}

        {!isLoading && query.length >= 2 && results.length > 0 && (
          <CommandGroup heading={`Results (${totalCount})`}>
            {results.map((result) => (
              <CommandItem
                key={result.id}
                value={`${result.text}-${result.id}`}
                onSelect={() => handleSelect(result)}
                className="flex flex-col items-start gap-1 py-3"
                data-testid={`search-result-${result.id}`}
              >
                <div className="flex items-center gap-2 w-full">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate flex-1">
                    <span className="text-muted-foreground">{result.projectName}</span>
                    <span className="text-muted-foreground/60 mx-1.5">&gt;</span>
                    <span className="font-medium">{result.text}</span>
                  </span>
                </div>
                {(result.status || result.details) && (
                  <div className="flex items-center gap-2 pl-6">
                    {result.status && (
                      <Badge className={`text-xs ${getStatusColor(result.status)}`}>
                        {result.status}
                      </Badge>
                    )}
                    {result.details && (
                      <span className="text-xs text-muted-foreground line-clamp-1">
                        {truncate(result.details)}
                      </span>
                    )}
                  </div>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {!isLoading && query.length < 2 && recentSearches.length > 0 && (
          <>
            <CommandGroup heading="Recent Searches">
              {recentSearches.map((recentQuery, index) => (
                <CommandItem
                  key={`recent-${index}`}
                  value={`recent-${recentQuery}`}
                  onSelect={() => handleRecentSearch(recentQuery)}
                  className="flex items-center gap-2"
                  data-testid={`recent-search-${index}`}
                >
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{recentQuery}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {!isLoading && query.length < 2 && recentSearches.length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Type at least 2 characters to search tasks...
          </div>
        )}
      </CommandList>
    </CommandDialog>
  );
}
